import React from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Settings,
  Users,
  CreditCard,
  ShieldCheck,
  FileText,
  PanelLeftClose,
  PanelLeftOpen,
  Lock,
  Puzzle,
  TrendingUp,
  Ruler,
  HardDrive,
  BookOpen,
  FolderOpen,
  X,
} from 'lucide-react';
import { getModuleIcon } from '@/lib/moduleIcons';
import { cn } from '@/lib/utils';
import OrganizationName from './OrganizationName';
import { useAuthStore } from '@/store/authStore';
import { useSystemStore } from '@/store/systemStore';
import { useUIStore } from '@/store/uiStore';
import { useOrganizationStore } from '@/store/organizationStore';
import { useSubscription } from '@/hooks/useBilling';
import { projectQuota } from '@/lib/projectQuota';
import { useModuleStore } from '@/store/moduleStore';
import { useOrgFormats } from '@/hooks/useOrgFormats';
import { useProjects } from '@/hooks/useProjects';
import { APP_VERSION, APP_SHA } from '@/config/version';
import { isBoqEditorPath } from '@/lib/projectTabs';

/**
 * T2656 — vstop »Deljeno z mano« v stranski vrstici uporabnika brez
 * organizacije. Ločena komponenta, ker poizvedbo o deljenih projektih (ista kot
 * na `NoOrganizationScreen`, zato iz predpomnilnika) potrebuje samo to stanje.
 * Brez deljenih projektov vstopa ni.
 */
function SharedProjectsNavLink({ collapsed, label }: { collapsed: boolean; label: string }) {
  const { data } = useProjects({ shared: true });
  if (!data?.results?.length) return null;
  return (
    <NavLink
      to="/projects?view=shared"
      data-testid="sidebar-shared-projects"
      title={collapsed ? label : undefined}
      className={({ isActive }) => cn(
        "flex items-center rounded-md text-body font-medium transition-colors",
        collapsed ? "justify-center p-2" : "gap-3 px-3 py-2",
        isActive
          ? "bg-[var(--bp-accent-soft)] text-[var(--bp-accent-text)]"
          : "text-muted-foreground hover:bg-accent hover:text-foreground"
      )}
    >
      <FolderOpen className="h-4 w-4 shrink-0" />
      {!collapsed && label}
    </NavLink>
  );
}

interface NavItem {
  icon: React.ElementType;
  label: string;
  path: string;
  slug?: string;
  isLocked?: boolean;
}

/**
 * T2422: oznake v seznamu povezav — naslov skupine (»Delo«, »Organizacija«)
 * in ločilna črta med skupinami. Niso povezave; razlikujemo jih po `kind`.
 */
interface NavMarker {
  kind: 'heading' | 'separator';
  key: string;
  label?: string;
}
type NavEntry = NavItem | NavMarker;

export default function Sidebar() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { settings } = useSystemStore();
  const {
    isSidebarCollapsed,
    toggleSidebar,
    isEditorSidebarCollapsed,
    toggleEditorSidebar,
    isSidebarHidden,
    isZenMode,
    openModuleTrial,
    isMobileSidebarOpen,
    setMobileSidebarOpen,
  } = useUIStore();
  // Zen Mode (T805) skrije Sidebar enako kot `isSidebarHidden` (maximize toggle).
  const hidden = isSidebarHidden || isZenMode;
  const { pathname } = useLocation();
  // T2410 (1): v urejevalniku popisa velja lastna, zapomnjena izbira (privzeto
  // skrčena) — tabela tam potrebuje širino. Preklop spremeni samo njo, zato se
  // vrstica ob vrnitvi na seznam projektov spet razširi.
  const onEditor = isBoqEditorPath(pathname);
  const collapsedPref = onEditor ? isEditorSidebarCollapsed : isSidebarCollapsed;
  const handleToggleCollapse = onEditor ? toggleEditorSidebar : toggleSidebar;
  // T1263: mobilni overlay VEDNO izriše razširjeno vsebino — strnjena (ikone
  // brez oznak) je desktop optimizacija in bi bila v overlayu širine w-64 le
  // pohabljen meni.
  const collapsed = collapsedPref && !isMobileSidebarOpen;

  // T1263: ob navigaciji se mobilni overlay zapre — cilj klika je vsebina
  // strani, ne meni čez njo. Varovalo prek ref-a: učinek ob montaži ne sme
  // zapreti pravkar odprtega menija, zapre ga šele DEJANSKA menjava poti.
  const prevPathnameRef = React.useRef(pathname);
  React.useEffect(() => {
    if (prevPathnameRef.current !== pathname) setMobileSidebarOpen(false);
    prevPathnameRef.current = pathname;
  }, [pathname, setMobileSidebarOpen]);
  const { currentOrganization, organizations, loaded } = useOrganizationStore();
  // T2656: isti pogoj kot zaslon brez organizacije v `DashboardLayout` (T2456)
  // — uspešno naložen, a prazen seznam. Napaka nalaganja tega ne pomeni.
  const hasNoOrganization = !!loaded && (organizations?.length ?? 0) === 0;
  const { data: subscription, isLoading: isSubscriptionLoading } = useSubscription(
    currentOrganization?.slug,
  );
  const { modules } = useModuleStore();
  const { formatDate: formatOrgDate } = useOrgFormats();

  // À la carte: no single "plan" name — label by subscribed module count.
  const subscribedCount = subscription?.subscribed_modules?.length ?? 0;

  // Veljavnost preizkusa MORA biti uporabniku vedno vidna, tudi če se naročnina
  // (Subscription) razlikuje od modulnega stanja. Zato datum izpeljemo tudi
  // neposredno iz modulov (vir resnice za dostop) — vzamemo najkasnejši trial
  // med `trialing` moduli. (T1071 — po T1040/T1070)
  const moduleTrialEndIso =
    Object.values(modules)
      .filter((m) => m.status === 'trialing' && m.trial_ends_at)
      .map((m) => m.trial_ends_at as string)
      .sort()
      .at(-1) ?? null;

  // T1716: `enforce_subscription_expiry` prestavi `trialing → expired`, modulov
  // pa ne obravnava (revokacija filtrira samo `cancelled`/`past_due`), zato
  // ostanejo `trialing`/`subscribed`. Brez te izjeme bi potekla naročnina
  // izpisala preizkus (iz modulov) ali »1 modul« — kot zdrava naročnina.
  const isExpired = subscription?.status === 'expired';
  const isTrial =
    !isSubscriptionLoading &&
    !isExpired &&
    (subscription?.status === 'trialing' || (subscribedCount === 0 && !!moduleTrialEndIso));
  const trialEndIso =
    subscription?.status === 'trialing'
      ? subscription?.trial_ends_at ?? moduleTrialEndIso
      : moduleTrialEndIso;
  const trialDaysLeft =
    isTrial && trialEndIso
      ? Math.max(0, Math.ceil((new Date(trialEndIso).getTime() - Date.now()) / 86_400_000))
      : null;

  // T1042 (a): stanje naročnine ima PREDNOST pred številom modulov.
  //
  // Prej je veja `subscribedCount > 0` stala pred `past_due` — in ker preklic
  // in zamuda plačila modulov ne odvzameta takoj (`OrganizationModuleAccess`
  // ostane `subscribed` do `current_period_end` pri preklicu oz. do
  // `current_period_end + grace` pri neplačilu, gl.
  // `billing/tasks.py::enforce_subscription_expiry`), je bil `package_past_due`
  // v praksi nedosegljiv: organizacija z zapadlim plačilom je videla mirno
  // »3 moduli«. Isto je veljalo za preklicano naročnino, ki se ne bo obnovila.
  //
  // `disputed` tu namenoma nima veje: chargeback zaklene organizacijo
  // (`billing/views.py::_handle_chargeback` → `org.is_active = False`), zato
  // uporabnik namesto stranske vrstice vidi zaslon blokiranega računa (T1192).
  // T2042: dokler odgovor `billing/<org>/subscription/` ni prišel, stanja NE
  // poznamo — prej je veja spodaj padla v `package_none` in takoj po prijavi
  // za trenutek izpisala »Brez naročnine« tudi organizaciji s potekom
  // naročnine. Nalaganje se ne sme prikazati kot trditev o stanju; enako ne
  // preizkus iz modulov, ker je naročnina lahko potekla (T1716).
  let subscriptionLabel: string | null;

  // T1042 (a): preklicana naročnina velja do konca plačanega obdobja
  // (`cancel_subscription()`; `Subscription.is_active`). Sama oznaka
  // »Preklicana naročnina« je zvenela kot konec dostopa — pod njo zato datum,
  // dokler obdobje še teče (ista oblika kot pri preizkusu, T2247).
  // Trenutek izrisa iz lenega začetnega stanja (ista oblika kot `BillingPage`)
  // — `Date.now()` v telesu izrisa ni čist (react-hooks/purity).
  const [renderedAt] = React.useState(() => Date.now());
  const cancelledAccessUntilIso =
    subscription?.status === 'cancelled' &&
    subscription.current_period_end &&
    new Date(subscription.current_period_end).getTime() > renderedAt
      ? subscription.current_period_end
      : null;

  // T2075: rep T2042 — kvota je med nalaganjem (in pri potekli naročnini, ki
  // v `limits` kvote nima) trdila »N / ∞«, ker je `max_projects || '∞'`
  // manjkajočo omejitev (in celo 0) prebral kot neomejeno. `∞` le, ko je
  // omejitev naložena in izrecno `null`; brez podatka o omejitvi samo število.
  // T2369 (N33): izračun je v `lib/projectQuota`, ker ga glava seznama
  // projektov deli — prej je imela svojo izpeljavo in kazala »16 / ∞«.
  const quota = projectQuota(currentOrganization?.project_count, subscription);
  const projectQuotaLabel = quota.label;
  const projectCount = quota.count;
  const maxProjects = quota.limit;
  if (isSubscriptionLoading) {
    subscriptionLabel = null;
  } else if (isTrial) {
    subscriptionLabel =
      trialDaysLeft != null
        ? t('sidebar.package_trial_days', { count: trialDaysLeft })
        : t('sidebar.package_trial');
  } else if (subscription?.status === 'past_due') {
    subscriptionLabel = t('sidebar.package_past_due');
  } else if (subscription?.status === 'cancelled') {
    subscriptionLabel = t('sidebar.package_cancelled');
  } else if (isExpired) {
    subscriptionLabel = t('sidebar.package_expired');
  } else if (subscribedCount > 0) {
    subscriptionLabel = t('sidebar.package_modules', { count: subscribedCount });
  } else {
    subscriptionLabel = t('sidebar.package_none');
  }

  const navItems: NavEntry[] = [
    { kind: 'heading', key: 'group-work', label: t('sidebar.group_work') },
    { icon: LayoutDashboard, label: t('navigation.dashboard'), path: '/dashboard' },
  ];
  // Navodila (Help center) — vedno dostopna povezava na dnu menija (T1134).
  const docsItem: NavItem = { icon: BookOpen, label: t('navigation.docs'), path: '/docs' };

  // T2656: brez organizacije moduli in administracija organizacije (Predloge,
  // Ekipa, Naročnina, Nastavitve) nimajo smisla — njihove poti tako ali tako
  // izrišejo zaslon brez organizacije. Ostanejo Nadzorna plošča (ta zaslon),
  // deljeni projekti (SharedProjectsNavLink) in Navodila.
  if (hasNoOrganization) {
    navItems.push(docsItem);
  } else {
    // Add modules dynamically from store, sorted by menu_config.sort_order.
    // Require a complete menu_config (path + label_key): a module whose
    // menu_config is empty/partial (e.g. installed before its menu.json
    // existed and not yet re-synced) would otherwise render a broken item
    // with no label and a fallback icon. Skip it until it is synced.
    Object.entries(modules)
      .filter(([, mod]) =>
        mod.menu_config?.path &&
        mod.menu_config?.label_key &&
        mod.status !== 'not_offered' &&
        mod.status !== 'dismissed'
      )
      .sort((a, b) => (a[1].menu_config?.sort_order ?? 99) - (b[1].menu_config?.sort_order ?? 99))
      .forEach(([slug, mod]) => {
        const cfg = mod.menu_config!;
        const isLocked = mod.status === 'offered' || mod.status === 'trial_expired';
        navItems.push({
          icon: getModuleIcon(cfg.icon),
          path: cfg.path,
          slug,
          label: t(cfg.label_key),
          isLocked,
        });
      });

    // Fixed items (administracija — vedno vidne; Templates ostane hardcoded,
    // ker ni samostojen modul — upravlja DocumentTemplate za BoQ exporte)
    // T2422: »Delo« (moduli, predloge) in »Organizacija« (ekipa, naročnina,
    // nastavitve) — naslovi in ločila so oznake v seznamu, ne nova logika.
    navItems.push(
      { icon: FileText, label: t('navigation.templates'), path: '/templates' },
      { kind: 'separator', key: 'group-org' },
      { kind: 'heading', key: 'group-org-heading', label: t('sidebar.group_org') },
      { icon: Users, label: t('navigation.team'), path: '/members' },
      { icon: CreditCard, label: t('navigation.billing'), path: '/billing' },
      { icon: Settings, label: t('navigation.settings'), path: '/settings' },
      { kind: 'separator', key: 'group-help' },
      docsItem,
    );
  }

  const env = settings?.environment_type || 'production';

  const envStyles = {
    development: {
      aside: "bg-[var(--bp-accent-soft)] border-[var(--bp-accent)]/30",
      badge: t('sidebar.env_dev'),
      badgeClass: "bg-[var(--bp-accent-soft)] text-[var(--bp-accent-text)]"
    },
    testing: {
      aside: "bg-fuchsia-50/50 dark:bg-fuchsia-950/40 border-fuchsia-200 dark:border-fuchsia-800",
      badge: t('sidebar.env_test'),
      badgeClass: "bg-fuchsia-100 dark:bg-fuchsia-900 text-fuchsia-700 dark:text-fuchsia-200"
    },
    production: {
      aside: "bg-card border-border",
      badge: null,
      badgeClass: ""
    }
  };

  const currentStyle = envStyles[env] || envStyles.production;

  return (
    <>
      {/* T1263: backdrop mobilnega overlaya — klik zunaj menija ga zapre. */}
      {isMobileSidebarOpen && (
        <div
          data-testid="sidebar-backdrop"
          aria-hidden="true"
          className="fixed inset-0 z-[60] bg-black/40 md:hidden"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}
    <aside
      // T2078: orientir `complementary` z imenom (prej brez njega).
      aria-label={t('sidebar.region_label')}
      className={cn(
      "flex-col h-screen border-r transition-all duration-200 ease-in-out shrink-0",
      // Desktop (md+): sticky stolpec z ročnim strni/razširi — nespremenjeno (T1263).
      "md:sticky md:top-0",
      hidden ? "md:w-0 md:border-0 md:overflow-hidden md:p-0" : collapsed ? "md:w-16" : "md:w-64",
      // Mobilno (<md): privzeto skrita; hamburger v glavi jo odpre kot overlay.
      // T2088: overlay se začne POD okoljsko pasico (`--env-banner-h`), sicer
      // pasica (`z-[200]`) prekrije glavo z imenom organizacije;
      // `h-auto` odpravi `h-screen`, ki bi ob zamiku segel čez spodnji rob.
      isMobileSidebarOpen
        ? "flex fixed top-[var(--env-banner-h,0px)] bottom-0 h-auto md:h-screen left-0 z-[70] w-64 max-w-[85vw] md:z-auto md:max-w-none"
        : "hidden md:flex w-64",
      currentStyle.aside
    )}>
      {/* Header */}
      <div className={cn(
        "flex items-center border-b border-border h-14",
        collapsed ? "justify-center px-0" : "justify-between px-5"
      )}>
        {collapsed ? (
          <>
            <img src="/logo-mark.svg" alt="BuildPoint" className="h-7 w-7 dark:hidden" />
            <img src="/logo-mark-on-dark.svg" alt="BuildPoint" className="h-7 w-7 hidden dark:block" />
          </>
        ) : (
          <div>
            <img src="/logo.svg" alt="BuildPoint" className="h-7 dark:hidden" />
            <img src="/logo-on-dark.svg" alt="BuildPoint" className="h-7 hidden dark:block" />
            {user?.is_site_admin && settings?.server_name && (
              <p className="text-caption text-muted-foreground font-mono leading-none mt-0.5">
                {settings.server_name}
              </p>
            )}
            <div className="flex items-center gap-1 mt-0.5 flex-wrap">
              {currentStyle.badge && (
                <div className={cn(
                  "px-1.5 py-0.5 rounded text-caption font-bold uppercase tracking-wider inline-block",
                  currentStyle.badgeClass
                )}>
                  {currentStyle.badge}
                </div>
              )}
              {settings?.is_maintenance_mode && (
                <div className="px-1.5 py-0.5 rounded text-caption font-bold uppercase tracking-wider inline-block bg-red-100 text-red-700 animate-pulse">
                  {t('sidebar.maintenance_badge')}
                </div>
              )}
            </div>
          </div>
        )}
        {/* Desktop: strni/razširi. Na mobilnem overlayu strnjenje nima smisla —
            tam stoji gumb za zapiranje (T1263). */}
        <button
          onClick={handleToggleCollapse}
          title={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
          aria-label={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
          aria-expanded={!collapsed}
          className="hidden md:block p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
        >
          {collapsed
            ? <PanelLeftOpen className="h-4 w-4" />
            : <PanelLeftClose className="h-4 w-4" />
          }
        </button>
        <button
          onClick={() => setMobileSidebarOpen(false)}
          title={t('sidebar.close_menu')}
          aria-label={t('sidebar.close_menu')}
          className="md:hidden p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* T2459: ime organizacije uporabnika (ena sama — brez izbirnika) */}
      <div className={cn("mt-3 mb-4", collapsed ? "px-2" : "px-3")}>
        <OrganizationName collapsed={collapsed} />
      </div>

      {/* Nav */}
      <nav className={cn("flex-1 space-y-1", collapsed ? "px-2" : "px-3")}>
        {navItems.map((entry: NavEntry) => {
          // T2422: naslov skupine je viden samo, ko je stranska vrstica razširjena;
          // ločilo med skupinami ostane tudi strnjeni vrstici (ikone).
          if ('kind' in entry) {
            if (entry.kind === 'separator') {
              return (
                <div
                  key={entry.key}
                  aria-hidden="true"
                  className={cn('my-2 border-t border-border', collapsed ? 'mx-2' : 'mx-3')}
                />
              );
            }
            return collapsed ? null : (
              <p
                key={entry.key}
                className="px-3 pt-1 pb-0.5 text-caption font-semibold text-muted-foreground"
              >
                {entry.label}
              </p>
            );
          }
          const item = entry;
          if (item.isLocked) {
            const lockedTitle = collapsed
              ? `${item.label} — ${t('modules.locked_tooltip')}`
              : t('modules.locked_tooltip');
            return (
              <button
                key={item.path || item.slug}
                onClick={() => openModuleTrial(item.slug ?? '')}
                title={lockedTitle}
                className={cn(
                  "w-full flex items-center rounded-md text-body font-medium transition-colors",
                  collapsed ? "justify-center p-2" : "gap-3 px-3 py-2",
                  "text-muted-foreground hover:bg-accent hover:text-foreground group"
                )}
              >
                <div className="relative">
                  <item.icon className="h-4 w-4 shrink-0" />
                  <div className="absolute -top-1.5 -right-1.5 bg-card rounded-full p-0.5 shadow-sm border border-border group-hover:border-muted-foreground/30">
                    <Lock className="h-2 w-2 text-[var(--bp-mute)]" />
                  </div>
                </div>
                {!collapsed && (
                  <span className="flex-1 text-left">{item.label}</span>
                )}
              </button>
            );
          }

          const link = (
            <NavLink
              key={item.path}
              to={item.path}
              title={collapsed ? item.label : undefined}
              className={({ isActive }) => cn(
                "flex items-center rounded-md text-body font-medium transition-colors",
                collapsed ? "justify-center p-2" : "gap-3 px-3 py-2",
                isActive
                  ? "bg-[var(--bp-accent-soft)] text-[var(--bp-accent-text)]"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground"
              )}
            >
              <item.icon className="h-4 w-4 shrink-0" />
              {!collapsed && item.label}
            </NavLink>
          );
          // T2656: deljeni projekti stojijo takoj pod Nadzorno ploščo.
          if (hasNoOrganization && item.path === '/dashboard') {
            return (
              <React.Fragment key={item.path}>
                {link}
                <SharedProjectsNavLink
                  collapsed={collapsed}
                  label={t('projects.shared_with_me')}
                />
              </React.Fragment>
            );
          }
          return link;
        })}

        {user?.is_site_admin && (
          <div className="pt-4 mt-4 border-t border-border">
            {!collapsed && (
              <p className="px-3 mb-2 text-caption font-bold text-muted-foreground uppercase tracking-widest">
                {t('navigation.sysadmin_section')}
              </p>
            )}
            <NavLink
              to="/sysadmin"
              end
              title={collapsed ? t('navigation.sysadmin') : undefined}
              className={({ isActive }) => cn(
                "flex items-center rounded-md text-body font-medium transition-colors",
                collapsed ? "justify-center p-2" : "gap-3 px-3 py-2",
                isActive
                  ? "bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground"
              )}
            >
              <ShieldCheck className="h-4 w-4 shrink-0" />
              {!collapsed && t('navigation.sysadmin')}
            </NavLink>
            <NavLink
              to="/sysadmin/users"
              title={collapsed ? t('navigation.sysadmin_users') : undefined}
              className={({ isActive }) => cn(
                "flex items-center rounded-md text-body font-medium transition-colors",
                collapsed ? "justify-center p-2" : "gap-3 px-3 py-2",
                isActive
                  ? "bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground"
              )}
            >
              <Users className="h-4 w-4 shrink-0" />
              {!collapsed && t('navigation.sysadmin_users')}
            </NavLink>
            <NavLink
              to="/sysadmin/modules"
              title={collapsed ? t('sysadmin.manage_modules') : undefined}
              className={({ isActive }) => cn(
                "flex items-center rounded-md text-body font-medium transition-colors",
                collapsed ? "justify-center p-2" : "gap-3 px-3 py-2",
                isActive
                  ? "bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground"
              )}
            >
              <Puzzle className="h-4 w-4 shrink-0" />
              {!collapsed && t('sysadmin.manage_modules')}
            </NavLink>
            <NavLink
              to="/sysadmin/finance"
              title={collapsed ? t('sysadmin.finance.nav') : undefined}
              className={({ isActive }) => cn(
                "flex items-center rounded-md text-body font-medium transition-colors",
                collapsed ? "justify-center p-2" : "gap-3 px-3 py-2",
                isActive
                  ? "bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground"
              )}
            >
              <TrendingUp className="h-4 w-4 shrink-0" />
              {!collapsed && t('sysadmin.finance.nav')}
            </NavLink>
            <NavLink
              to="/sysadmin/units"
              title={collapsed ? t('sysadmin.units.nav') : undefined}
              className={({ isActive }) => cn(
                "flex items-center rounded-md text-body font-medium transition-colors",
                collapsed ? "justify-center p-2" : "gap-3 px-3 py-2",
                isActive
                  ? "bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground"
              )}
            >
              <Ruler className="h-4 w-4 shrink-0" />
              {!collapsed && t('sysadmin.units.nav')}
            </NavLink>
            <NavLink
              to="/sysadmin/storage"
              title={collapsed ? t('sysadmin.storage.nav') : undefined}
              className={({ isActive }) => cn(
                "flex items-center rounded-md text-body font-medium transition-colors",
                collapsed ? "justify-center p-2" : "gap-3 px-3 py-2",
                isActive
                  ? "bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground"
              )}
            >
              <HardDrive className="h-4 w-4 shrink-0" />
              {!collapsed && t('sysadmin.storage.nav')}
            </NavLink>
          </div>
        )}
      </nav>

      {/* Package status — T2656: brez organizacije ni paketa ne kvote. */}
      {!collapsed && !hasNoOrganization && (
        <div className="p-4 border-t border-border flex flex-col gap-3">
          <div className="bg-muted rounded-lg p-3">
            <p className="text-dense font-semibold text-muted-foreground uppercase mb-1">{t('sidebar.package_status')}</p>
            {subscriptionLabel === null ? (
              <p
                data-testid="sidebar-package-loading"
                aria-busy="true"
                className="h-5 flex items-center"
              >
                <span className="h-3 w-24 rounded bg-border animate-pulse" aria-hidden="true" />
              </p>
            ) : (
              <p className="text-body font-medium text-foreground">{subscriptionLabel}</p>
            )}
            {isTrial && trialEndIso && (
              <p className="text-caption text-muted-foreground mt-0.5">
                {/* T2247: ista oblika kot na zaslonu Naročnina — nastavitev organizacije. */}
                {t('sidebar.package_trial_until', {
                  date: formatOrgDate(trialEndIso),
                })}
              </p>
            )}
            {!isSubscriptionLoading && cancelledAccessUntilIso && (
              <p
                data-testid="sidebar-cancelled-until"
                className="text-caption text-muted-foreground mt-0.5"
              >
                {t('sidebar.package_cancelled_access_until', {
                  date: formatOrgDate(cancelledAccessUntilIso),
                })}
              </p>
            )}
            {isSubscriptionLoading ? (
              <div data-testid="sidebar-quota-loading" aria-busy="true" className="mt-2">
                <span className="block h-1.5 w-full rounded-full bg-border animate-pulse" aria-hidden="true" />
                <span className="mt-1.5 block h-2.5 w-28 rounded bg-border animate-pulse" aria-hidden="true" />
              </div>
            ) : (
              <>
                <div className="mt-2 h-1.5 w-full bg-border rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all duration-500"
                    style={{
                      width: `${typeof maxProjects === 'number' && maxProjects > 0 ? Math.min(100, (projectCount / maxProjects) * 100) : 0}%`,
                    }}
                  />
                </div>
                <p data-testid="sidebar-quota" className="text-caption text-muted-foreground mt-1">
                  {projectQuotaLabel} {t('sidebar.projects_of_org')}
                </p>
              </>
            )}
          </div>
          
          <div className="flex justify-between items-center text-caption text-muted-foreground px-1">
            {/* T3122: različica po datumu deploya, SHA v title. */}
            <span title={APP_SHA || undefined} className="cursor-default hover:text-foreground transition-colors">
              {t('sidebar.version', { version: APP_VERSION })}
            </span>
          </div>
        </div>
      )}
      {collapsed && !hasNoOrganization && (
        <div className="p-3 border-t border-border flex flex-col items-center gap-3">
          <div 
            title={
              subscriptionLabel === null
                ? undefined
                : `${subscriptionLabel} — ${projectQuotaLabel}`
            }
            className="h-2 w-2 rounded-full bg-primary" 
          />
          <span
            title={[t('sidebar.version', { version: APP_VERSION }), APP_SHA].filter(Boolean).join(' · ')}
            className="text-caption text-muted-foreground cursor-default"
          >
            {APP_VERSION}
          </span>
        </div>
      )}
    </aside>
    </>
  );
}
